export type ConsentCategory = 'essential' | 'analytics' | 'functionality' | 'advertising';

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  functionality: boolean;
  advertising: boolean;
}

export interface ConsentRecord {
  timestamp: string;
  preferences: ConsentPreferences;
  version: string;
  gpcEnabled: boolean;
  bannerShown: boolean;
}

export interface PolicyVersion {
  version: string;
  effective_date: string;
  description: string;
  requires_reconsent: boolean;
}

const CONSENT_COOKIE_NAME = 'df_consent';
const CONSENT_EXPIRY_DAYS = 365;
let cachedConsentVersion: string | null = null;

export const DEFAULT_PREFERENCES: ConsentPreferences = {
  essential: true,
  analytics: false,
  functionality: false,
  advertising: false,
};

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function deleteCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

export async function getActiveConsentVersion(): Promise<string> {
  if (cachedConsentVersion) {
    return cachedConsentVersion;
  }

  try {
    const { supabase } = await import('./supabase');
    const { data, error } = await supabase
      .rpc('get_active_consent_version')
      .maybeSingle();

    if (error || !data) {
      console.warn('Failed to fetch active consent version, using default:', error);
      cachedConsentVersion = '1.0';
      return '1.0';
    }

    cachedConsentVersion = data.version;
    return data.version;
  } catch (error) {
    console.error('Error fetching consent version:', error);
    cachedConsentVersion = '1.0';
    return '1.0';
  }
}

export function clearVersionCache(): void {
  cachedConsentVersion = null;
}

export function detectGPC(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as any;
  return nav.globalPrivacyControl === true;
}

export async function getConsentRecord(): Promise<ConsentRecord | null> {
  try {
    const cookieValue = getCookie(CONSENT_COOKIE_NAME);
    console.log('[getConsentRecord] Raw cookie value:', cookieValue);
    if (!cookieValue) return null;

    const decoded = decodeURIComponent(cookieValue);
    console.log('[getConsentRecord] Decoded:', decoded);
    const record: ConsentRecord = JSON.parse(decoded);
    console.log('[getConsentRecord] Parsed record:', record);

    const currentVersion = await getActiveConsentVersion();
    if (record.version !== currentVersion) {
      console.log('[getConsentRecord] Version mismatch:', record.version, 'vs', currentVersion);
      return null;
    }

    const recordDate = new Date(record.timestamp);
    const now = new Date();
    const daysDiff = (now.getTime() - recordDate.getTime()) / (1000 * 60 * 60 * 24);

    if (daysDiff > CONSENT_EXPIRY_DAYS) {
      console.log('[getConsentRecord] Record expired');
      return null;
    }

    return record;
  } catch (error) {
    console.error('Error reading consent record:', error);
    return null;
  }
}

export async function saveConsentRecord(preferences: ConsentPreferences, bannerShown: boolean = true): Promise<void> {
  const currentVersion = await getActiveConsentVersion();
  const record: ConsentRecord = {
    timestamp: new Date().toISOString(),
    preferences,
    version: currentVersion,
    gpcEnabled: detectGPC(),
    bannerShown,
  };

  console.log('[saveConsentRecord] Saving record:', record);
  const encoded = encodeURIComponent(JSON.stringify(record));
  console.log('[saveConsentRecord] Encoded:', encoded);
  setCookie(CONSENT_COOKIE_NAME, encoded, CONSENT_EXPIRY_DAYS);
  console.log('[saveConsentRecord] Cookie set, verifying...');

  const verification = getCookie(CONSENT_COOKIE_NAME);
  console.log('[saveConsentRecord] Verification read:', verification);

  setCookie('df_seen_banner', 'true', CONSENT_EXPIRY_DAYS);

  await logConsentToDatabase(preferences, detectGPC(), currentVersion);
  console.log('[saveConsentRecord] Complete');
}

export function clearConsentRecord(): void {
  deleteCookie(CONSENT_COOKIE_NAME);
  deleteCookie('df_seen_banner');
}

export async function hasConsent(category: ConsentCategory): Promise<boolean> {
  if (category === 'essential') return true;

  const record = await getConsentRecord();
  if (!record) return false;

  return record.preferences[category] === true;
}

export async function shouldShowBanner(): Promise<boolean> {
  const record = await getConsentRecord();
  return record === null;
}

export async function getConsentPreferences(): Promise<ConsentPreferences> {
  const record = await getConsentRecord();
  const prefs = record ? record.preferences : DEFAULT_PREFERENCES;
  console.log('[getConsentPreferences] Returning:', prefs);
  return prefs;
}

export async function acceptAllConsent(): Promise<void> {
  const preferences: ConsentPreferences = {
    essential: true,
    analytics: true,
    functionality: true,
    advertising: true,
  };
  await saveConsentRecord(preferences);
}

export async function rejectNonEssentialConsent(): Promise<void> {
  const preferences: ConsentPreferences = {
    essential: true,
    analytics: false,
    functionality: false,
    advertising: false,
  };
  await saveConsentRecord(preferences);
}

export async function applyGPCOptOut(): Promise<void> {
  const preferences: ConsentPreferences = {
    essential: true,
    analytics: false,
    functionality: false,
    advertising: false,
  };
  await saveConsentRecord(preferences, false);
}

async function logConsentToDatabase(preferences: ConsentPreferences, gpcEnabled: boolean, version: string): Promise<void> {
  try {
    const visitorId = localStorage.getItem('visitor_id') || 'anonymous';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    const { supabase } = await import('./supabase');

    await supabase.from('consent_logs').insert({
      visitor_id: visitorId,
      consent_version: version,
      preferences,
      gpc_enabled: gpcEnabled,
      user_agent: userAgent,
    });
  } catch (error) {
    console.error('Failed to log consent to database:', error);
  }
}

export function clearNonEssentialStorage(): void {
  try {
    const keysToRemove: string[] = [];

    const essentialKeys = [
      CONSENT_COOKIE_NAME,
      'df_seen_banner',
    ];

    const isSupabaseKey = (key: string): boolean => {
      return key.startsWith('sb-') ||
             key.startsWith('supabase.') ||
             key.startsWith('supabase-auth-token') ||
             key.includes('auth-token');
    };

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !essentialKeys.includes(key) && !isSupabaseKey(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));

    if (typeof sessionStorage !== 'undefined') {
      const sessionKeysToRemove: string[] = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && !isSupabaseKey(key)) {
          sessionKeysToRemove.push(key);
        }
      }

      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    }
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
}

export async function saveVehiclePreference(year: number, make: string, model: string): Promise<void> {
  const hasFunctionalityConsent = await hasConsent('functionality');
  if (!hasFunctionalityConsent) {
    return;
  }

  const vehicle = { year, make, model };
  const encoded = encodeURIComponent(JSON.stringify(vehicle));
  setCookie('df_saved_vehicle', encoded, 180);
}

export async function getVehiclePreference(): Promise<{ year: number; make: string; model: string } | null> {
  const hasFunctionalityConsent = await hasConsent('functionality');
  if (!hasFunctionalityConsent) {
    return null;
  }

  try {
    const cookieValue = getCookie('df_saved_vehicle');
    if (!cookieValue) return null;

    const decoded = decodeURIComponent(cookieValue);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error reading vehicle preference:', error);
    return null;
  }
}

export function clearVehiclePreference(): void {
  deleteCookie('df_saved_vehicle');
}

export const CONSENT_CATEGORIES = {
  essential: {
    id: 'essential' as ConsentCategory,
    name: 'Strictly Necessary',
    description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot disable these cookies.',
    required: true,
    cookies: [
      { name: 'df_consent', purpose: 'Stores your cookie preferences', duration: '12 months' },
      { name: 'df_seen_banner', purpose: 'Remembers that you have seen the cookie banner', duration: '12 months' },
    ],
  },
  analytics: {
    id: 'analytics' as ConsentCategory,
    name: 'Performance & Analytics',
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the site.',
    required: false,
    cookies: [
      { name: 'visitor_id', purpose: 'Anonymous visitor identification for analytics', duration: 'Persistent' },
      { name: 'session_id', purpose: 'Session tracking for page view analytics', duration: 'Session' },
    ],
  },
  functionality: {
    id: 'functionality' as ConsentCategory,
    name: 'Functionality',
    description: 'These cookies allow the website to remember choices you make and provide enhanced, personalized features.',
    required: false,
    cookies: [
      { name: 'df_saved_vehicle', purpose: 'Saves your vehicle (Year/Make/Model) for convenience', duration: '6 months' },
    ],
  },
  advertising: {
    id: 'advertising' as ConsentCategory,
    name: 'Advertising & Affiliates',
    description: 'These cookies are used to track affiliate link clicks and measure advertising effectiveness. They help us earn commissions when you purchase recommended tools. California residents: This may constitute "sharing" under CCPA, as data like your IP address, referrer URL, and device information may be disclosed to affiliate partners (e.g., Amazon Associates) for cross-context behavioral advertising.',
    required: false,
    cookies: [
      { name: 'affiliate_click', purpose: 'Tracks affiliate link interactions', duration: '30 days' },
      { name: 'utm_*', purpose: 'Marketing campaign tracking parameters', duration: 'Session' },
    ],
  },
};
